import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, ArrowLeft, FileText } from 'lucide-react';
import { getCurrentTerms, acceptTerms, getMyTermsStatus } from '../../api/General/termsAndPolicies';
import type { TermsAndPoliciesView } from '../../models/general';
import { clearOnboardingGateCache } from '../../components/OnboardingGate';

export default function TermsAcceptanceView() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [terms, setTerms] = useState<TermsAndPoliciesView | null>(null);
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [scrolledToBottom, setScrolledToBottom] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [alreadyAccepted, setAlreadyAccepted] = useState(false);

    const scrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [cur, status] = await Promise.all([getCurrentTerms(), getMyTermsStatus()]);
                setTerms(cur.payload ?? null);
                if (status.payload?.hasAccepted) {
                    setAlreadyAccepted(true);
                    setScrolledToBottom(true);
                }
            } catch {
                setError(t('Failed to load Terms & Policies'));
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        if (atBottom) setScrolledToBottom(true);
    };

    const handleAccept = async () => {
        setAccepting(true);
        setError(null);
        try {
            const res = await acceptTerms();
            if (res.errors?.length) {
                setError(res.errors[0].description ?? t('Failed to accept terms'));
            } else {
                // Acceptance flips the server-side `isBlocked` flag. Drop the
                // gate's cached `blocked=true` so the next navigation sees the
                // fresh status — without this, the gate bounces the user back
                // here for up to 30s.
                clearOnboardingGateCache();
                navigate('/onboarding/complete', { replace: true });
            }
        } catch {
            setError(t('Failed to accept terms'));
        } finally {
            setAccepting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-10" data-testid="terms-acceptance-view">
            <div className="max-w-3xl w-full bg-white rounded-xl shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <FileText className="w-6 h-6 text-gray-600" />
                        <h1 className="text-xl font-semibold">{t('Terms & Policies')}</h1>
                        {terms && <span className="text-xs text-gray-500" data-testid="terms-version-badge">v{terms.version}</span>}
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                    >
                        <ArrowLeft className="w-4 h-4" /> {t('Back')}
                    </button>
                </div>

                {loading && <div className="text-sm text-gray-500">{t('Loading…')}</div>}

                {!loading && !terms && (
                    <div className="p-4 rounded-md border border-yellow-200 bg-yellow-50 text-sm text-yellow-800">
                        {t('Your company has not published any Terms & Policies yet.')}
                    </div>
                )}

                {!loading && terms && (
                    <>
                        <div
                            ref={scrollRef}
                            onScroll={handleScroll}
                            data-testid="terms-content-scroller"
                            className="border border-border rounded-md p-4 bg-gray-50 max-h-[50vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed"
                        >
                            {terms.content}
                        </div>

                        {!scrolledToBottom && (
                            <div className="mt-2 text-xs text-amber-600">
                                {t('Please scroll to the bottom to enable the acceptance checkbox.')}
                            </div>
                        )}

                        {error && <div className="mt-3 p-3 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-sm">{error}</div>}

                        <label className={`mt-4 flex items-center gap-2 text-sm ${scrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                            <input
                                type="checkbox"
                                data-testid="terms-confirm-checkbox"
                                disabled={!scrolledToBottom || alreadyAccepted}
                                checked={confirmed || alreadyAccepted}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                className="w-4 h-4"
                            />
                            <span>{t('I have read and accept the Terms & Policies')}</span>
                        </label>

                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                data-testid="terms-accept-button"
                                onClick={handleAccept}
                                disabled={accepting || (!confirmed && !alreadyAccepted) || alreadyAccepted}
                                className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <ShieldCheck className="w-4 h-4" />
                                {alreadyAccepted ? t('Already accepted') : accepting ? t('Accepting…') : t('Accept')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
